import {
  applyActiveMatchHighlight,
  tHighlightEnvironment,
} from '../ChatSearch/ChatSearch.highlight';

/**
 * Jumping the open thread to one specific chat event (SRE-3328).
 *
 * This module is the ONE place that decides loaded-vs-not for a requested
 * `eventId`. Everything else — the row that was clicked, the provider that
 * carries the target across the navigation, the container that owns the node
 * map — only feeds it. When the `?around=<eventId>` proxy lands on bff-web,
 * the `'not-loaded'` branch of `focusChatEvent` is the single function that
 * changes: fetch the page around the event, re-render, resolve the node again.
 *
 * Deliberately DOM-level rather than React state: the highlight is a marker on
 * one bubble, and driving it through state would re-render every bubble in the
 * thread twice for a tint that belongs to exactly one of them.
 *
 * Unrelated to `highlightMessages` in MessageBubbleMenu.hook — that is the
 * "destacar mensaje" favourite, a backend mutation. Nothing here touches it.
 */

/**
 * Class the matched bubble carries while the highlight is on.
 *
 * It no longer draws the purple ring: the "you are here" signal is now the
 * underlined active mark over the matched words (ChatSearch.highlight.ts). The
 * class stays as the state marker, and paints a neutral fallback tint only
 * where the browser has no Custom Highlight API to draw that mark with.
 */
export const FOCUSED_EVENT_CLASS = 'MessageContainer__FocusedEvent';

/**
 * How often the settle watcher re-measures. Short enough that a correction
 * lands within a frame or two of the reflow that caused it, long enough that
 * the whole window costs a handful of layout reads.
 */
export const FOCUS_SETTLE_POLL_MS = 80;

/**
 * How long the anchor must hold still — same offset, same content height —
 * before the thread counts as settled and the watcher stops.
 */
export const FOCUS_SETTLE_QUIET_MS = 300;

/**
 * Hard deadline for the watcher. Something that never stops moving (an
 * animated GIF that resizes, a media element that keeps re-laying-out) must
 * not leave an interval running for the rest of the session.
 */
export const FOCUS_SETTLE_MAX_MS = 8000;

/**
 * A correction is withheld this long unless a reflow has already been seen.
 * A browser's own smooth scroll spends a few hundred ms with the anchor still
 * off screen, and correcting during it would convert every jump into a
 * teleport. A reflow lifts the grace immediately, because a reflow is exactly
 * what invalidates the in-flight animation's destination.
 */
export const FOCUS_SETTLE_GRACE_MS = 600;

/**
 * How much of the anchor has to be inside the scroll port for the jump to
 * count as landed: all of it, or 60% of the port for a bubble taller than
 * that. Expressed as a fraction rather than "centred" on purpose — an anchor
 * near either end of the thread CANNOT be centred, and demanding it would
 * re-scroll forever.
 */
export const FOCUS_VISIBLE_FRACTION = 0.6;

/**
 * Gestures that end the highlight.
 *
 * All four are things the USER did. `scroll` is deliberately absent: the
 * watcher below scrolls the thread itself, and listening for it would have the
 * correction dismiss the very highlight it exists to keep on screen.
 */
export const FOCUS_DISMISS_EVENTS = [
  'wheel',
  'touchmove',
  'keydown',
  'pointerdown',
] as const;

/**
 * Marks a region whose gestures must NOT end the highlight: the in-chat search
 * bar ("Buscar en esta conversación").
 *
 * The bar is where the user asks for the NEXT match, so typing in its field,
 * pressing Enter or clicking ∧ ∨ are the very gestures that follow a jump. Were
 * they to dismiss it, the mark would come off the match the moment the user
 * looked at it — and every keystroke of a refined query would clear it too.
 */
export const FOCUS_DISMISS_EXEMPT_ATTRIBUTE = 'data-chat-search';

/**
 * Did this gesture start inside a region that keeps the highlight?
 *
 * @param target - The event target, which may be a text node or null.
 * @returns true when the gesture must leave the highlight on.
 */
export function isFocusDismissExempt(target: EventTarget | null): boolean {
  const element =
    target instanceof Element
      ? target
      : target instanceof Node
        ? target.parentElement
        : null;

  return Boolean(element?.closest(`[${FOCUS_DISMISS_EXEMPT_ATTRIBUTE}]`));
}

/**
 * 'jumped'     — the event was already in the DOM; it was scrolled to and lit.
 * 'not-loaded' — the event is further back than the pages currently loaded.
 *                The thread pages newest-first by cursor, so this is the normal
 *                outcome for an old hit. The caller answers it by fetching the
 *                anchored window (`?around=`) ONCE and letting the re-render
 *                bring the event into the DOM, then asking again. It must NOT
 *                chain cursor pages hunting for the event: that is unbounded
 *                work behind a spinner, and is exactly what `?around=` replaces.
 *                When the window cannot be had, the caller degrades to a hint
 *                rather than silently leaving the user at the bottom.
 */
export type tFocusOutcome = 'jumped' | 'not-loaded';

export type tFocusChatEventResult = {
  outcome: tFocusOutcome;
  /** Drops the highlight and the settle watcher (unmount, or a newer jump). */
  cancel: () => void;
};

/** Why the settle watcher stopped. Reported for tests and instrumentation. */
export type tFocusSettleReason = 'settled' | 'timeout' | 'dismissed';

export type tFocusChatEventParams = {
  eventId: string;
  /**
   * Words to mark on the jumped-to bubble with the active search mark, for
   * the life of the highlight — an inbox result's snippet terms. Absent for
   * the in-chat search, which keeps its own active mark.
   */
  terms?: string[];
  /** The highlight registry; defaults to the platform's. Injected by tests. */
  highlightEnvironment?: tHighlightEnvironment | null;
  /** Node lookup for an event id — the container's ref map, in practice. */
  resolveNode: (eventId: string) => HTMLElement | null | undefined;
  /**
   * The thread's scroll port. Without it the anchor cannot be measured, so the
   * jump degrades to the single scroll it used to be — correct on a text-only
   * thread, and no worse than before anywhere else.
   */
  container?: HTMLElement | null;
  /**
   * Honour `prefers-reduced-motion`. Passed in rather than read here so the
   * decision stays pure and the tests do not need a matchMedia stub.
   */
  reducedMotion?: boolean;
  /** Fired when the highlight comes off, for tests and instrumentation. */
  onHighlightCleared?: () => void;
  /** Fired when the settle watcher stops, with the reason it stopped. */
  onSettled?: (reason: tFocusSettleReason) => void;
};

const noop = () => {};

/**
 * Reads the user's motion preference, defaulting to "animate" when the query is
 * unavailable (jsdom, old browsers) — the same default the platform uses.
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
    return false;

  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * Is enough of the anchor inside the scroll port to say the jump landed?
 *
 * "Enough" rather than "centred" because an anchor within half a viewport of
 * either end of the thread cannot be centred, and a predicate that demanded it
 * would re-scroll on every tick forever. A zero-height port means there is no
 * layout to judge by (jsdom, a hidden thread) and the answer is yes: nothing
 * can be corrected from measurements that do not exist.
 *
 * @param node - The anchor bubble.
 * @param container - The thread's scroll port.
 * @returns true when the jump may be considered landed.
 */
export function eventIsVisible(
  node: HTMLElement,
  container: HTMLElement,
): boolean {
  const anchor = node.getBoundingClientRect();
  const port = container.getBoundingClientRect();

  if (!port.height) return true;

  const overlap =
    Math.min(anchor.bottom, port.bottom) - Math.max(anchor.top, port.top);
  const required = Math.min(
    anchor.height,
    port.height * FOCUS_VISIBLE_FRACTION,
  );

  return overlap >= required - 1;
}

/**
 * Can any media still resolve its dimensions and move the anchor?
 *
 * This is the "slower connection" question, and it is the reason the watcher
 * cannot simply stop at the first quiet moment. A bubble's `<img>` has no
 * intrinsic height until it decodes, so a thread of screenshots is quiet, then
 * grows by hundreds of pixels, then is quiet again — and on a slow link the
 * gap between those is seconds, not the 60 ms measured on the preview.
 *
 * Only media at or above the bottom of the scroll port counts. Two things
 * follow from that, and both were measured. Content BELOW the fold can only
 * extend scrollHeight downwards; it cannot move what is on screen, so waiting
 * on it buys nothing. And the thread's images are `loading="lazy"`, so ones
 * well below the fold never start loading and never report complete — gating
 * on them would hold the watcher open to its deadline on every single jump
 * into a thread of screenshots.
 *
 * @param container - The thread's scroll port.
 * @returns true when nothing that could move the anchor is still resolving.
 */
export function mediaIsSettled(container: HTMLElement): boolean {
  const fold = container.getBoundingClientRect().bottom;

  const canStillMoveTheAnchor = (element: Element) =>
    element.getBoundingClientRect().top <= fold;

  const images = container.querySelectorAll('img');

  for (const image of images)
    if (!image.complete && canStillMoveTheAnchor(image)) return false;

  const videos = container.querySelectorAll('video');

  // HAVE_METADATA. Dimensions are known from there on, which is all the layout
  // needs; waiting for frames would keep the watcher alive for the whole clip.
  for (const video of videos)
    if (video.readyState < 1 && canStillMoveTheAnchor(video)) return false;

  return true;
}

/**
 * Keeps the anchor on screen while the thread's layout is still moving.
 *
 * Measured on the preview against a thread of screenshots: the jump lands
 * correctly (anchor at y=530 in a 620 px port) and then loses it. Two things
 * happen after the scroll is issued. The images decode and the content grows
 * — 1744 px to 3223 px of scrollHeight across 60 ms — and the browser's own
 * scroll anchoring absorbs that, which is fine. Then the smooth scroll
 * animation, still in flight and still carrying the destination offset it
 * computed against the PRE-decode layout, overrides that correction and drives
 * scrollTop from 1049 back down to 490, leaving the anchor 812 px below the
 * fold. The scroll "succeeded"; the destination stopped being the right one
 * while it was on its way there.
 *
 * So the fix cannot be a second scroll on a timer: the timer would have to
 * outlast both the decode and the animation, and on a slower connection it
 * would not. It has to be measurement — hold the anchor in view until the
 * layout stops moving, then stop looking. "Stopped moving" means quiet AND no
 * image or video still resolving its dimensions (see mediaIsSettled), because
 * a slow link makes a thread of screenshots look quiet for seconds at a time.
 *
 * Corrections are issued with `behavior: 'auto'`. A smooth correction would
 * carry a destination of its own and reintroduce exactly the failure above.
 *
 * @param params - The anchor, its port, and the settle callback.
 * @returns A stop handle.
 */
function watchAnchorStaysVisible(params: {
  node: HTMLElement;
  container: HTMLElement;
  onSettled?: (reason: tFocusSettleReason) => void;
}): (reason?: tFocusSettleReason) => void {
  const { node, container, onSettled } = params;

  if (typeof node.scrollIntoView !== 'function') return noop;

  const startedAt = Date.now();
  let lastMovedAt = startedAt;
  let lastTop = Math.round(node.getBoundingClientRect().top);
  let lastHeight = container.scrollHeight;
  let sawReflow = false;
  let stopped = false;

  const stop = (reason?: tFocusSettleReason) => {
    if (stopped) return;
    stopped = true;
    clearInterval(timer);
    if (reason) onSettled?.(reason);
  };

  const timer = setInterval(() => {
    const now = Date.now();
    const height = container.scrollHeight;
    const top = Math.round(node.getBoundingClientRect().top);

    if (height !== lastHeight) sawReflow = true;

    // Any movement at all — the content growing under the anchor, or the
    // anchor sliding because an animation is still running — restarts the
    // quiet window. This is what keeps the watcher alive through the stale
    // animation described above, which moves the anchor without ever changing
    // the content height.
    if (height !== lastHeight || top !== lastTop) lastMovedAt = now;

    lastHeight = height;
    lastTop = top;

    if (!eventIsVisible(node, container)) {
      // Before a reflow has been seen, an off-screen anchor is most likely the
      // browser's smooth scroll still travelling. Correcting there would cancel
      // it and make every jump a teleport, so the grace period waits it out.
      if (sawReflow || now - startedAt >= FOCUS_SETTLE_GRACE_MS) {
        node.scrollIntoView({ block: 'center', behavior: 'auto' });
        lastMovedAt = now;
        lastTop = Math.round(node.getBoundingClientRect().top);
      }
    } else if (
      now - lastMovedAt >= FOCUS_SETTLE_QUIET_MS &&
      mediaIsSettled(container)
    ) {
      stop('settled');
      return;
    }

    if (now - startedAt >= FOCUS_SETTLE_MAX_MS) stop('timeout');
  }, FOCUS_SETTLE_POLL_MS);

  return stop;
}

/**
 * Scrolls the thread to `eventId`, keeps it there while the layout settles,
 * and highlights it — or reports that the event is not loaded.
 *
 * The highlight has no timer. It is the only thing on screen saying WHICH
 * message matched, and a timer means the answer is gone for anyone whose eye
 * took a moment to travel. It comes off when the user does something instead:
 * any of FOCUS_DISMISS_EVENTS, or `cancel()` for an unmount, a thread switch
 * or a newer jump.
 *
 * @param params - See tFocusChatEventParams.
 * @returns The outcome and a cancel handle for the highlight and the watcher.
 */
export function focusChatEvent(
  params: tFocusChatEventParams,
): tFocusChatEventResult {
  const {
    eventId,
    resolveNode,
    container,
    reducedMotion = false,
    terms,
    highlightEnvironment,
    onHighlightCleared,
    onSettled,
  } = params;

  const node = eventId ? resolveNode(eventId) : null;

  if (!node) return { outcome: 'not-loaded', cancel: noop };

  // `scrollIntoView` is absent in jsdom and in a detached node; a missing jump
  // must not take the highlight down with it.
  if (typeof node.scrollIntoView === 'function') {
    node.scrollIntoView({
      block: 'center',
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
  }

  node.classList.add(FOCUSED_EVENT_CLASS);

  // Same lifetime as the class: off on the user's next gesture, a newer jump,
  // a thread switch or an unmount.
  const clearMark = terms?.length
    ? applyActiveMatchHighlight({
        node,
        terms,
        environment: highlightEnvironment,
      })
    : noop;

  let released = false;

  const stopWatching: (reason?: tFocusSettleReason) => void = container
    ? watchAnchorStaysVisible({ node, container, onSettled })
    : noop;

  const release = (dismissed: boolean) => {
    if (released) return;
    released = true;

    for (const type of FOCUS_DISMISS_EVENTS)
      window.removeEventListener(type, dismiss, true);

    // A user gesture ends the watcher too: they have taken the scroll back, and
    // a correction after that would fight them for it.
    stopWatching(dismissed ? 'dismissed' : undefined);
    node.classList.remove(FOCUSED_EVENT_CLASS);
    clearMark();
  };

  function dismiss(event: Event) {
    if (isFocusDismissExempt(event.target)) return;

    release(true);
    onHighlightCleared?.();
  }

  for (const type of FOCUS_DISMISS_EVENTS)
    window.addEventListener(type, dismiss, { capture: true, passive: true });

  return {
    outcome: 'jumped',
    cancel: () => release(false),
  };
}

/**
 * What an `?around=` fetch produced, as far as the jump is concerned.
 *
 * 'loaded'      — a usable window came back and contains the anchor.
 * 'unavailable' — the anchor is gone (404) or belongs to another chat (400).
 *                 Never retried: the answer will not change.
 * 'failed'      — the call did not complete (network, 5xx, 424).
 *
 * All three degrade to the same hint on screen; they are kept apart because
 * "no such message" and "the request did not finish" are different facts, and
 * collapsing them is how a permanent failure gets retried forever.
 */
export type tAnchorLoadOutcome = 'loaded' | 'unavailable' | 'failed';

/**
 * Reads the HTTP status off whatever the request layer rejected with.
 *
 * bff-web deliberately preserves 404 and 400 for this route instead of folding
 * them into its usual 424, precisely so this side can tell them apart.
 */
export function classifyAnchorLoadError(error: unknown): tAnchorLoadOutcome {
  const status = (
    error as { response?: { status?: number }; status?: number } | null
  )?.response?.status;

  const code = status ?? (error as { status?: number } | null)?.status;

  return code === 404 || code === 400 ? 'unavailable' : 'failed';
}

/**
 * Decides whether an `?around=` response may be used as the anchored window.
 *
 * The anchor MUST come back inside the window. This is not defensive padding:
 * a bff-web that does not yet know the `around` key ignores it and answers with
 * the newest page — a 200, correctly shaped, and completely wrong. Accepting it
 * would scroll the user to the bottom of the thread while the code believed it
 * had jumped, which is the one failure mode the endpoint contract singles out
 * ("never a silent fallback"). An unknown query key is dropped silently by
 * every layer in between, so the only place this can be caught is here, by
 * looking for the event we asked for.
 *
 * @param eventId - The anchor that was requested.
 * @param events - The window as returned, in any order.
 * @returns true when the window genuinely contains the anchor.
 */
export function windowContainsAnchor(
  eventId: string,
  events: ReadonlyArray<{ id: string }> | null | undefined,
): boolean {
  if (!eventId || !events?.length) return false;

  return events.some((event) => event.id === eventId);
}

/**
 * A tiny registry of live bubble nodes, keyed by event id.
 *
 * `register(id, null)` is React detaching the ref, and drops the entry. React
 * runs every detach in the mutation phase before any attach in the layout
 * phase, so a bubble that unmounts and remounts in the same commit ends with
 * the live node registered, not with a hole.
 *
 * Entries are therefore self-cleaning: a thread switch unmounts every bubble
 * and empties the map without anyone having to remember to.
 */
export function createEventNodeRegistry() {
  const nodes = new Map<string, HTMLElement>();

  return {
    register(eventId: string, node: HTMLElement | null) {
      if (!eventId) return;

      if (node) {
        nodes.set(eventId, node);
        return;
      }

      nodes.delete(eventId);
    },
    resolve(eventId: string) {
      return nodes.get(eventId) ?? null;
    },
    clear() {
      nodes.clear();
    },
    get size() {
      return nodes.size;
    },
  };
}

export type tEventNodeRegistry = ReturnType<typeof createEventNodeRegistry>;
